/**
 * Result Confirmation
 * Read-only scoreboard shown before a match result is submitted. Lays out each
 * team's score per set/game and highlights the detected winner so a reversed
 * ("upside down") entry is obvious before it reaches the server.
 */

"use client";

import { matchResultToString } from "@/lib/sports";
import {
  PadelMatchResult,
  PickleballMatchResult,
  Sport,
  SportMatchResult,
} from "@/types";
import { useTranslations } from "next-intl";
import React from "react";

interface ResultConfirmationProps {
  sport: Sport["slug"];
  result: SportMatchResult;
  team1Name?: string;
  team2Name?: string;
  team1Id?: string;
  team2Id?: string;
  winnerId: string;
}

interface ScoreColumn {
  label: string;
  team1: number;
  team2: number;
  tieBreak?: { team1Points: number; team2Points: number };
}

/** Build per-set/per-game columns from a result, dropping unplayed entries. */
function buildColumns(
  sport: Sport["slug"],
  result: SportMatchResult,
  setLabel: string,
  gameLabel: string,
): ScoreColumn[] {
  if (sport === "padel") {
    const r = result as PadelMatchResult;
    const sets = [r.set1, r.set2, r.set3];
    // Sets 1 and 2 are always played; set 3 only when it has a score.
    return sets
      .filter((s, i) => i < 2 || s.team1Games > 0 || s.team2Games > 0)
      .map((s, i) => ({
        label: `${setLabel} ${i + 1}`,
        team1: s.team1Games,
        team2: s.team2Games,
        tieBreak: s.tieBreak,
      }));
  }

  const r = result as PickleballMatchResult;
  return r.games
    .filter((g) => g.team1Points > 0 || g.team2Points > 0)
    .map((g, i) => ({
      label: `${gameLabel} ${i + 1}`,
      team1: g.team1Points,
      team2: g.team2Points,
    }));
}

const ResultConfirmation: React.FC<ResultConfirmationProps> = ({
  sport,
  result,
  team1Name,
  team2Name,
  team1Id,
  winnerId,
}) => {
  const t = useTranslations();

  const columns = buildColumns(
    sport,
    result,
    t("matches.set"),
    t("matches.game"),
  );

  const name1 = team1Name ?? "";
  const name2 = team2Name ?? "";
  const team1Won = winnerId === team1Id;
  const winnerName = team1Won ? name1 : name2;

  const summary = matchResultToString(result, sport);

  const rows: {
    name: string;
    scores: { value: number; tieBreak?: number }[];
    isWinner: boolean;
  }[] = [
    {
      name: name1,
      scores: columns.map((c) => ({
        value: c.team1,
        tieBreak: c.tieBreak?.team1Points,
      })),
      isWinner: team1Won,
    },
    {
      name: name2,
      scores: columns.map((c) => ({
        value: c.team2,
        tieBreak: c.tieBreak?.team2Points,
      })),
      isWinner: !team1Won,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--text-secondary)]">
        {t("matches.confirmResultSubtitle")}
      </p>

      <div className="overflow-hidden rounded-lg border border-[var(--border-subtle)]">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[var(--bg-elevated)]">
              <th
                scope="col"
                className="px-3 py-2 text-left font-cinzel text-xs uppercase tracking-wide text-[var(--text-secondary)]"
              >
                {t("teams.team")}
              </th>
              {columns.map((c) => (
                <th
                  key={c.label}
                  scope="col"
                  className="px-2 py-2 text-center font-cinzel text-xs uppercase tracking-wide text-[var(--text-secondary)] whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.name}
                className={
                  row.isWinner
                    ? "bg-[var(--gold)]/10 border-l-4 border-[var(--gold)]"
                    : "border-l-4 border-transparent"
                }
              >
                <th
                  scope="row"
                  className={`px-3 py-2 text-left font-semibold ${
                    row.isWinner
                      ? "text-[var(--gold-dark)] dark:text-[var(--gold)]"
                      : "text-[var(--text-primary)]"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {row.isWinner && (
                      <span aria-hidden="true" className="text-base leading-none">
                        👑
                      </span>
                    )}
                    <span className="break-words">{row.name}</span>
                    {row.isWinner && (
                      <span className="ml-1 rounded-full bg-[var(--gold)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black">
                        {t("matches.winner")}
                      </span>
                    )}
                  </span>
                </th>
                {row.scores.map((s, i) => (
                  <td
                    key={i}
                    className={`px-2 py-2 text-center tabular-nums ${
                      row.isWinner
                        ? "font-bold text-[var(--gold-dark)] dark:text-[var(--gold)]"
                        : "text-[var(--text-primary)]"
                    }`}
                  >
                    {s.value}
                    {s.tieBreak !== undefined && (
                      <span className="ml-0.5 text-[10px] align-super text-[var(--text-secondary)]">
                        {s.tieBreak}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-center font-cinzel text-base text-[var(--text-primary)]">
        <span className="font-bold text-[var(--gold-dark)] dark:text-[var(--gold)]">
          {t("matches.teamWins", { team: winnerName })}
        </span>{" "}
        <span className="text-[var(--text-secondary)]">{summary}</span>
      </p>
    </div>
  );
};

export default ResultConfirmation;
